import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import LockIcon from "@mui/icons-material/Lock";
//Limitless imports
import { RESPOND, REVIEW, REPORTED, FORWARDED, CLAIM_TIMEOUT, CLOSED, CLAIMED_BY_ANOTHER, CLAIMED_FOR_REVIEW, CLAIMED_FOR_FORWARD_REVIEW, REVIEWED, DISAGREED, UNAVAILABLE, VIEW_AS_MANAGER, VIEW_DIALOGUE_OPTIONS_AS_MANAGER, REVIEW_AS_MANAGER, REVIEW_ESCALATION_AS_MANAGER, RESPOND_CLIENT_ACTION, DETAILED_REVIEW_AS_MANAGER, SILENT_CLAIM_TIMEOUT, isReadOnlyState, isMessageReviewState, REVIEW_DETAILED, CLAIMED_FOR_REVIEW_DETAILED, QUALITY_REVIEW_AS_MANAGER } from "../../..";
import { withi18n } from "../../../../customer/components";
import { ExpertConversationFinalDialog, PortalButton } from "../../..";
import FooterCountdownButton from "./FooterCountdownButton";
const useStyles = makeStyles(theme => ({
  readOnlyFooterContainer: {
    width: "calc(100% - 16px)",
    left: 8,
    bottom: 0,
    position: "absolute",
    zIndex: 1101,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    flexDirection: "column"
  },
  none: {
    height: 0
  },
  small: {
    height: 80
  },
  medium: {
    height: 160
  },
  reportedIcon: {
    color: theme.background.dark
  },
  padding: {
    paddingBottom: 10
  },
  title: {
    textAlign: "center",
    marginLeft: 10,
    marginRight: 10
  },
  iconSize: {
    fontSize: 40
  }
}));
const ReadOnlyFooter = ({
  t,
  message,
  onSwitchToRespond,
  onClaimMessage,
  clearMessage
}) => {
  var _message$conversation;
  const [hasPressed, setHasPressed] = React.useState(false);
  const classes = useStyles();
  React.useMemo(() => {
    setHasPressed(false);
  }, [(_message$conversation = message.conversationState) === null || _message$conversation === void 0 ? void 0 : _message$conversation.state]);
  const clearMessageAction = {
    footerSize: "small",
    buttonText: "Clear",
    buttonPress: clearMessage,
    id: "clearButton",
    shouldCountdown: true
  };
  const sendReply = {
    footerSize: "small",
    buttonText: "Send Reply",
    buttonPress: onSwitchToRespond,
    id: "respondBtn"
  };
  const handleClaimMessage = () => {
    setHasPressed(true);
    onClaimMessage(message.messageId);
  };
  const RESPOND_FOOTER = {
    footerSize: "small",
    buttonText: "Respond",
    buttonPress: handleClaimMessage,
    id: "respondBtn"
  };
  const FOOTER_MAPPINGS = {
    [RESPOND_CLIENT_ACTION]: RESPOND_FOOTER,
    [REVIEW]: {
      footerSize: "small",
      buttonText: "Review",
      buttonPress: handleClaimMessage,
      id: "reviewBtn"
    },
    [REVIEW_DETAILED]: {
      footerSize: "small",
      buttonText: "Review",
      buttonPress: handleClaimMessage,
      id: "reviewBtn"
    },
    [REPORTED]: clearMessageAction,
    [FORWARDED]: clearMessageAction,
    [REVIEWED]: clearMessageAction,
    [CLAIM_TIMEOUT]: {
      buttonText: "Finish",
      buttonPress: clearMessage,
      body: "Your claim on this conversation has timed out",
      title: "Claim Timeout",
      showDialog: true,
      id: "finishButton"
    },
    [SILENT_CLAIM_TIMEOUT]: {
      buttonText: "Finish",
      buttonPress: clearMessage,
      body: "Your claim on this conversation has timed out. This was likely due to internet connectivity issues. Your last dialogue has been copied to your clipboard.",
      title: "Claim Timeout",
      showDialog: true,
      id: "finishButton"
    },
    [CLAIMED_BY_ANOTHER]: {
      footerSize: "medium",
      buttonText: "Clear",
      buttonPress: clearMessage,
      body: "This conversation has been claimed by another ambassador",
      icon: /*#__PURE__*/React.createElement(LockIcon, {
        className: classes.iconSize
      }),
      id: "clearButton",
      shouldCountdown: true
    },
    [CLOSED]: {
      footerSize: "small",
      buttonText: "Finish",
      buttonPress: clearMessage,
      body: "We are sorry, but this conversation is no longer available to answer as it has been closed.",
      title: "Task Closed",
      showDialog: true,
      id: "clearButton",
      shouldCountdown: true
    },
    [CLAIMED_FOR_REVIEW]: sendReply,
    [CLAIMED_FOR_FORWARD_REVIEW]: {
      footerSize: "none"
    },
    [CLAIMED_FOR_REVIEW_DETAILED]: {
      footerSize: "none"
    },
    [DISAGREED]: sendReply,
    [UNAVAILABLE]: {
      buttonText: "Finish",
      buttonPress: clearMessage,
      body: "Sorry, this task requires a review by Limitless and is no longer available",
      title: "Task Unavailable",
      showDialog: true,
      shouldCountdown: true,
      id: "finishUnavailable"
    },
    [VIEW_AS_MANAGER]: {
      footerSize: "none"
    },
    [VIEW_DIALOGUE_OPTIONS_AS_MANAGER]: {
      footerSize: "none"
    },
    [REVIEW_AS_MANAGER]: {
      footerSize: "none"
    },
    [REVIEW_ESCALATION_AS_MANAGER]: {
      footerSize: "none"
    },
    [DETAILED_REVIEW_AS_MANAGER]: {
      footerSize: "none"
    },
    [QUALITY_REVIEW_AS_MANAGER]: {
      footerSize: "none"
    }
  };
  let footer = FOOTER_MAPPINGS[message.conversationState.state];
  footer = footer ? footer : FOOTER_MAPPINGS[CLOSED];
  if (isReadOnlyState(message.conversationState.previousState) && !isMessageReviewState(message.conversationState.previousState) && footer.showDialog) {
    footer = {
      ...footer,
      buttonText: "Clear",
      showDialog: false,
      body: null
    };
  }
  //if we're reviewing a resolved message, the expert cannot reply
  if (isMessageReviewState(message.conversationState.state) && message.custResolved) {
    footer = {
      footerSize: "small"
    };
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, !footer.showDialog && /*#__PURE__*/React.createElement("div", {
    className: `${classes.readOnlyFooterContainer} ${classes[footer.footerSize]}`
  }, footer.body && /*#__PURE__*/React.createElement(Typography, {
    className: classes.title,
    gutterBottom: footer.icon ? false : true
  }, t(footer.body)), footer.icon, footer.subheading && /*#__PURE__*/React.createElement(Typography, null, t(footer.subheading)), footer.buttonText && !footer.shouldCountdown && /*#__PURE__*/React.createElement(PortalButton, {
    id: footer.id,
    onClick: footer.buttonPress,
    disabled: hasPressed
  }, footer.buttonText), footer.buttonText && footer.shouldCountdown && /*#__PURE__*/React.createElement(FooterCountdownButton, {
    id: footer.id,
    onClick: footer.buttonPress,
    disabled: hasPressed,
    label: footer.buttonText
  })), footer.showDialog && /*#__PURE__*/React.createElement(ExpertConversationFinalDialog, {
    buttonText: footer.buttonText,
    onFinishClick: footer.buttonPress,
    title: footer.title,
    body: footer.body
  }));
};
export default withi18n(ReadOnlyFooter);
ReadOnlyFooter.propTypes = {
  t: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  onClaimMessage: PropTypes.func.isRequired,
  clearMessage: PropTypes.func.isRequired,
  onSwitchToRespond: PropTypes.func.isRequired
};